import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faEye, faLock, faMicrophone, faNoteSticky, faPen, faStop } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useApplicationContext } from 'ApplicationContext';
import { useDictation } from 'Hooks/useDictation';
import { MarkdownEditor } from 'Components/MarkdownEditor';
import { MarkdownRenderer } from 'Components/MarkdownRenderer';

interface CallNotesPanelProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

/**
 * Interne Gesprächsnotizen — der Block, in den man während eines Telefonats tippt.
 *
 * Der Inhalt ist Markdown, genau wie die Angebotstexte: Der KI-Assistent hängt seine
 * Gesprächsnotiz als Markdown an, und über mehrere Telefonate wächst daraus ein Text mit
 * Zwischenüberschriften und Listen. Deshalb hier dieselben zwei Werkzeuge wie im Rest der
 * Kalkulation — MDXEditor zum Schreiben, MarkdownRenderer zum Lesen.
 *
 * Voreingestellt ist die Schreibansicht, nicht die Leseansicht: Wer am Telefon mitschreibt, soll
 * nicht erst klicken müssen. Der Editor stellt Überschriften und Listen ohnehin schon dar, die
 * Leseansicht ist für „durchlesen, was beim letzten Mal besprochen wurde".
 *
 * Ebenfalls bewusst nicht eingeklappt: eine Notiz, die man erst aufklappen muss, schreibt im
 * Telefonat niemand.
 */
export const CallNotesPanel: React.FC<CallNotesPanelProps> = ({ value, onChange, disabled }) => {
    const { t } = useTranslation();
    const { api } = useApplicationContext();
    const [reading, setReading] = useState(false);

    /** Nachtragen heißt schreiben — sonst landet Diktiertes unsichtbar hinter der Leseansicht. */
    const appendAndEdit = (next: string) => {
        setReading(false);
        onChange(next);
    };

    // Diktiertes wird an das Bestehende angehängt, nicht ersetzt — im Gespräch wird mehrfach
    // nachgetragen.
    const dictation = useDictation(api, (text) =>
        appendAndEdit(value.trim().length > 0 ? `${value.replace(/\s+$/, '')} ${text}` : text));

    /**
     * Setzt „#### TT.MM.JJJJ HH:MM" als neue Zeile ans Ende. Notizen wachsen über mehrere
     * Telefonate; ohne Zeitstempel weiß hinterher niemand, was wann gesagt wurde. Als Überschrift
     * und nicht als bloße Textzeile, damit die Gespräche in der Leseansicht sichtbar getrennt sind.
     */
    const addTimestamp = () => {
        const stamp = new Date().toLocaleString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
        const prefix = value.trim().length > 0 ? `${value.replace(/\s+$/, '')}\n\n` : '';
        appendAndEdit(`${prefix}#### ${stamp}\n\n`);
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', rowGap: 1 }}
            >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Box sx={{ color: 'primary.main' }}>
                        <FontAwesomeIcon icon={faNoteSticky} />
                    </Box>
                    <Typography variant="h6">{t('calculation.notes.title')}</Typography>
                    <Chip
                        size="small"
                        variant="outlined"
                        icon={<FontAwesomeIcon icon={faLock} style={{ fontSize: '0.75rem' }} />}
                        label={t('calculation.notes.internal')}
                    />
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        color={dictation.recording ? 'error' : 'primary'}
                        disabled={disabled || dictation.busy}
                        onClick={dictation.recording ? dictation.stop : dictation.start}
                        startIcon={<FontAwesomeIcon icon={dictation.recording ? faStop : faMicrophone} />}
                    >
                        {dictation.recording
                            ? t('calculation.notes.stopDictation')
                            : dictation.busy
                                ? t('calculation.notes.transcribing')
                                : t('calculation.notes.dictate')}
                    </Button>
                    <Button
                        size="small"
                        disabled={disabled}
                        onClick={addTimestamp}
                        startIcon={<FontAwesomeIcon icon={faClock} />}
                    >
                        {t('calculation.notes.timestamp')}
                    </Button>
                    <Button
                        size="small"
                        onClick={() => setReading((r) => !r)}
                        startIcon={<FontAwesomeIcon icon={reading ? faPen : faEye} />}
                    >
                        {reading ? t('calculation.notes.edit') : t('calculation.notes.read')}
                    </Button>
                </Stack>
            </Stack>

            {reading ? (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, minHeight: 120 }}>
                    {value.trim()
                        ? <MarkdownRenderer>{value}</MarkdownRenderer>
                        : <Typography variant="body2" color="text.secondary">{t('calculation.notes.empty')}</Typography>}
                </Box>
            ) : (
                <MarkdownEditor
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    placeholder={t('calculation.notes.placeholder')}
                    minHeight={140}
                />
            )}

            {dictation.error && (
                <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                    {dictation.error}
                </Alert>
            )}

            <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                {t('calculation.notes.hint')}
            </Alert>
        </Paper>
    );
};

export default CallNotesPanel;
